/**
 * PeopleAutocomplete — Searchable people selector with "Create new person" option
 * S10 Wave 2
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { authFetchJson } from '../../lib/authFetch'
import type { ProjectPersonRole } from '@hos/shared/constants'
import { PROJECT_PERSON_ROLES, PROJECT_PERSON_ROLE_LABELS } from '@hos/shared/constants'

interface Person {
  id: number
  name: string
  email?: string
  title?: string
}

interface PeopleAutocompleteProps {
  value: Person | null
  onChange: (person: Person | null) => void
  placeholder?: string
  allowCreate?: boolean
  selectedRole?: ProjectPersonRole
  onRoleChange?: (role: ProjectPersonRole) => void
}

interface CreatePersonModalState {
  open: boolean
  name: string
  email: string
  title: string
  saving: boolean
  error: string | null
}

export function PeopleAutocomplete({
  value,
  onChange,
  placeholder = 'Search people...',
  allowCreate = true,
  selectedRole = 'researcher',
  onRoleChange,
}: PeopleAutocompleteProps) {
  const [query, setQuery] = useState(value?.name || '')
  const [results, setResults] = useState<Person[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [modal, setModal] = useState<CreatePersonModalState>({
    open: false,
    name: '',
    email: '',
    title: '',
    saving: false,
    error: null,
  })
  const ref = useRef<HTMLDivElement>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  // Fetch people on query change
  useEffect(() => {
    if (!query.trim()) {
      setResults([])
      return
    }

    setIsLoading(true)
    const signal = abortControllerRef.current?.signal || new AbortController().signal

    const API_BASE = import.meta.env.VITE_API_URL || ''
    authFetchJson<{ people: Person[] }>(
      `${API_BASE}/api/v2/people?q=${encodeURIComponent(query)}`,
      { signal }
    )
      .then((data) => {
        setResults(data.people || [])
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          console.error('Failed to fetch people:', err)
        }
      })
      .finally(() => setIsLoading(false))
  }, [query])

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelectPerson = (person: Person) => {
    onChange(person)
    setQuery(person.name)
    setIsOpen(false)
  }

  const handleCreateClick = () => {
    setModal({ ...modal, open: true, name: query })
  }

  const handleCreatePerson = async () => {
    if (!modal.name.trim()) return

    setModal({ ...modal, saving: true, error: null })

    try {
      const newPerson = await authFetchJson<Person>('/api/v2/people', {
        method: 'POST',
        json: {
          name: modal.name.trim(),
          email: modal.email.trim() || undefined,
          title: modal.title.trim() || undefined,
        },
      })

      onChange(newPerson)
      setQuery(newPerson.name)
      setIsOpen(false)
      setModal({
        open: false,
        name: '',
        email: '',
        title: '',
        saving: false,
        error: null,
      })
      setResults([])
    } catch (err: any) {
      setModal({
        ...modal,
        saving: false,
        error: err.message || 'Failed to create person',
      })
    }
  }

  const showCreateOption = allowCreate && query.trim() && !results.some((p) => p.name.toLowerCase() === query.toLowerCase())

  return (
    <>
      <div ref={ref} className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setIsOpen(true)
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className="
            w-full px-3 py-2
            bg-[#12122a] border border-[#2a2a44]
            rounded-lg
            text-[#e8e8f0] text-[13px]
            placeholder:text-[#5a5a6c]
            focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50
          "
        />
        {isOpen && (results.length > 0 || showCreateOption || isLoading) && (
          <div className="
            absolute z-50 mt-1 w-full
            bg-[#1a1a32] border border-[#2a2a44]
            rounded-lg shadow-lg
            max-h-[280px] overflow-y-auto
          ">
            {isLoading && (
              <div className="px-3 py-3 text-center text-[13px] text-[#8b8ba0]">
                Loading...
              </div>
            )}

            {results.map((person) => (
              <button
                key={person.id}
                onClick={() => handleSelectPerson(person)}
                className="
                  w-full text-left px-3 py-3
                  hover:bg-[#2a2a44]
                  border-b border-[#2a2a44] last:border-b-0
                  transition-colors
                "
              >
                <div className="text-[13px] font-medium text-[#e8e8f0]">
                  {person.name}
                </div>
                {(person.email || person.title) && (
                  <div className="text-[12px] text-[#8b8ba0] mt-1">
                    {person.email && <div>{person.email}</div>}
                    {person.title && <div>{person.title}</div>}
                  </div>
                )}
              </button>
            ))}

            {showCreateOption && (
              <button
                onClick={handleCreateClick}
                className="
                  w-full text-left px-3 py-3
                  hover:bg-[#2a2a44]
                  border-t border-[#2a2a44]
                  transition-colors
                  text-[13px] text-[#8b8ba0]
                "
              >
                <span className="text-[#E61B25] font-medium">+</span> Create new person "{query}"
              </button>
            )}
          </div>
        )}
      </div>

      {/* Create Person Modal */}
      {modal.open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
          <div className="
            bg-[#1a1a2e] border border-[#2a2a44]
            rounded-lg shadow-xl
            p-6 w-[90%] max-w-[400px]
            space-y-4
          ">
            <h3 className="text-[15px] font-semibold text-[#e8e8f0]">
              Create New Person
            </h3>

            {modal.error && (
              <div className="px-3 py-2 bg-red-900/30 border border-red-700/50 rounded-lg text-[12px] text-red-300">
                {modal.error}
              </div>
            )}

            <div>
              <label className="text-[12px] font-medium text-[#a8a8c0] block mb-1">
                Name <span className="text-[#E61B25]">*</span>
              </label>
              <input
                type="text"
                value={modal.name}
                onChange={(e) => setModal({ ...modal, name: e.target.value })}
                placeholder="Full name"
                className="
                  w-full px-3 py-2
                  bg-[#12122a] border border-[#2a2a44]
                  rounded-lg
                  text-[#e8e8f0] text-[13px]
                  focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50
                "
              />
            </div>

            <div>
              <label className="text-[12px] font-medium text-[#a8a8c0] block mb-1">
                Email
              </label>
              <input
                type="email"
                value={modal.email}
                onChange={(e) => setModal({ ...modal, email: e.target.value })}
                placeholder="person@example.com"
                className="
                  w-full px-3 py-2
                  bg-[#12122a] border border-[#2a2a44]
                  rounded-lg
                  text-[#e8e8f0] text-[13px]
                  focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50
                "
              />
            </div>

            <div>
              <label className="text-[12px] font-medium text-[#a8a8c0] block mb-1">
                Title
              </label>
              <input
                type="text"
                value={modal.title}
                onChange={(e) => setModal({ ...modal, title: e.target.value })}
                placeholder="e.g., PhD Student, Professor"
                className="
                  w-full px-3 py-2
                  bg-[#12122a] border border-[#2a2a44]
                  rounded-lg
                  text-[#e8e8f0] text-[13px]
                  focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50
                "
              />
            </div>

            <div className="flex gap-2 pt-2">
              <button
                onClick={handleCreatePerson}
                disabled={!modal.name.trim() || modal.saving}
                className="
                  flex-1 px-4 py-2
                  bg-[#E61B25] text-white
                  rounded-lg
                  text-[13px] font-medium
                  hover:bg-[#d41620]
                  disabled:opacity-50 disabled:cursor-not-allowed
                  transition-colors
                "
              >
                {modal.saving ? 'Creating...' : 'Create'}
              </button>
              <button
                onClick={() =>
                  setModal({
                    open: false,
                    name: '',
                    email: '',
                    title: '',
                    saving: false,
                    error: null,
                  })
                }
                className="
                  flex-1 px-4 py-2
                  bg-[#12122a] border border-[#2a2a44]
                  rounded-lg
                  text-[13px] font-medium text-[#a8a8c0]
                  hover:bg-[#1a1a2e] hover:border-[#3a3a54]
                  transition-colors
                "
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
